import { Trans } from 'react-i18next';
import { styled } from '@mui/material/styles';
import { Typography } from '@components/Typography';
import { ExternalLink } from '@components/Link';
import { useTranslation, useQueryParams } from '@hooks';
import pluspointLogo from 'assets/images/pluspoint-logo.svg?url';
import { PresenceIcon, ClockIcon, SmileCropIcon } from 'assets/images/icons';
import authGb from 'assets/images/auth-gb.webp';
import authEs from 'assets/images/auth-es.webp';
import authPt from 'assets/images/auth-pt.webp';
import authUa from 'assets/images/auth-ua.webp';
import { LANGUAGE } from '@constants/language';

const Root = styled('div')({
    backgroundColor: '#F1F4F5',
    minWidth: '527px',
    height: '100vh',
    display: 'flex',
    flexWrap: 'wrap',
    width: '100%',
    '@media (max-width: 500px)': {
        minWidth: 'auto',
    },
    '@media (max-width: 1024px)': {
        height: 'var(--100vh)',
        overflow: 'auto',
        minWidth: 'auto',
    },
});

const PluspointLogo = styled('div')({
    width: '200px',
    height: '50px',
    marginBottom: '30px',
    backgroundImage: `url(${pluspointLogo})`,
    backgroundSize: 'contain',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
});

const LeftColumn = styled('div')({
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '50%',
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    '@media (max-width: 1024px)': {
        maxWidth: 'none',
    },
});

const RightColumn = styled('div')({
    color: 'var(--text-primary)',
    width: '100%',
    height: '100%',
    maxWidth: '50%',
    display: 'grid',
    gridTemplateRows: '50% 50%',
    justifyContent: 'center',
    '@media (max-width: 1024px)': {
        display: 'none',
    },
});

const ContentWrapper = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    paddingTop: 'min(13.1vh, 118px)',
    paddingBottom: 'min(6.2vh, 56px)',
    '@media (max-width: 1150px)': {
        justifyContent: 'flex-end',
    },
});

const BlocksWrapper = styled('div')({
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 32,
    marginTop: 'min(9.1vh, 82px)',
    '@media (max-width: 1220px)': {
        gap: 16,
        marginTop: '32px',
    },
});

const Block = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flex: '1 1 181px',
    maxWidth: '181px',
    minWidth: '181px',
    '@media (max-width: 1150px)': {
        flex: '1 1 150px',
        maxWidth: '150px',
        minWidth: '150px',
    },
});

const IconWrapper = styled('div')({
    width: 24,
    height: 24,
    marginBottom: 8,
    '& svg path': {
        fill: '#5BA479',
    },
});

const AuthImage = styled('img')({
    maxWidth: '100%',
    width: '100%',
    margin: 'auto auto 0',
    maxHeight: '100%',
    objectFit: 'contain',
});

const AuthImageMap = {
    [LANGUAGE.ENGLISH]: authGb,
    EN: authGb, // INFO: Mock only for auth pages because EN key is not used in the app
    [LANGUAGE.SPANISH]: authEs,
    [LANGUAGE.PORTUGUESE]: authPt,
    [LANGUAGE.UKRAINIAN]: authUa,
    [LANGUAGE.PORTUGUESE_BRAZIL]: authPt,
    [LANGUAGE.SPANISH_MEXICO]: authEs,
};

export const AuthLayout = ({ children, showLogo = true }) => {
    const query = useQueryParams();
    const lng = query.get('lng') || '';
    const isPtLocale = [LANGUAGE.PORTUGUESE, LANGUAGE.PORTUGUESE_BRAZIL].includes(lng);
    const { t, i18n } = useTranslation();

    const blocks = [
        {
            icon: <SmileCropIcon />,
            title: t('AuthLayout.block1Title'),
            description: t('AuthLayout.block1Description'),
        },
        {
            icon: <ClockIcon />,
            title: t('AuthLayout.block2Title'),
            description: t('AuthLayout.block2Description'),
        },
        {
            icon: <PresenceIcon />,
            title: t('AuthLayout.block3Title'),
            description: t('AuthLayout.block3Description'),
        },
    ];
    return (
        <Root>
            <LeftColumn>
                {showLogo && (
                    <ExternalLink href={`https://www.pluspoint.io/${isPtLocale ? 'pt' : '?r=0'}`} target="_blank">
                        <PluspointLogo />
                    </ExternalLink>
                )}
                {children}
            </LeftColumn>
            <RightColumn>
                <ContentWrapper>
                    <Typography variant="title1" sx={{ textAlign: 'center' }}>
                        <Trans t={t} i18nKey="AuthLayout.title">
                            <span
                                style={{
                                    backgroundClip: 'text',
                                    backgroundImage: 'linear-gradient(118deg,#1f4c5c,#aed8c0)',
                                    WebkitTextFillColor: 'transparent',
                                }}
                            >
                                {{ reputation: t('AuthLayout.onlineReputation') }}
                            </span>
                        </Trans>
                    </Typography>
                    <BlocksWrapper>
                        {blocks.map((block, index) => (
                            <Block key={index}>
                                <IconWrapper>{block.icon}</IconWrapper>
                                <Typography
                                    variant="subtitle1"
                                    sx={{
                                        letterSpacing: '0 !important',
                                        fontWeight: '600 !important',
                                        marginBottom: '8px',
                                        textAlign: 'center',
                                        '@media (max-width: 1150px)': {
                                            fontSize: '14px !important',
                                            lineHeight: '20px !important',
                                        },
                                    }}
                                >
                                    {block.title}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{ letterSpacing: '0 !important', display: 'block', textAlign: 'center' }}
                                >
                                    {block.description}
                                </Typography>
                            </Block>
                        ))}
                    </BlocksWrapper>
                </ContentWrapper>
                <AuthImage src={AuthImageMap[i18n.language]} alt="Auth" />
            </RightColumn>
        </Root>
    );
};
